import { emptyImage2, videoThumbnailImage } from '@anytag/shared-assets';
import {
  cn,
  getDecodedUrlFileName,
  getFileTypeFromUrl,
  IMAGE_FILE_TYPES,
  ImageFileType,
  VIDEO_TYPES_SET,
  type VideoSetType
} from '@anytag/shared-utils';
import Zoom from 'react-medium-image-zoom';
import { Icon } from '../../Icon';
import { ProgressBar } from '../../ProgressBar';
import { SortableDragArea, SortableItem } from '../../SortableList';
import 'react-medium-image-zoom/dist/styles.css';

export interface PreviewItemProps {
  id: number;
  url: string;
  preview: string;
  disabled?: boolean;
  className?: string;
  onDelete: () => void;
  widthPercentage: string;
  uploadProgress?: { progress: number; progressUrl: string | null };
  hasZoomPreview?: boolean;
}

export const PreviewItem = ({
  id,
  url,
  preview,
  onDelete,
  disabled,
  className,
  uploadProgress,
  widthPercentage,
  hasZoomPreview
}: PreviewItemProps) => {
  const fileType = getFileTypeFromUrl<VideoSetType | ImageFileType>(url);
  const isImage = IMAGE_FILE_TYPES.has(fileType as ImageFileType);
  const isVideo = VIDEO_TYPES_SET.has(fileType as VideoSetType);
  const isEmptyImage = !(isImage || isVideo);
  const imagePreview = preview && isImage ? preview : emptyImage2;
  const hasProgressBar = url === uploadProgress?.progressUrl && uploadProgress?.progress !== 100;

  return (
    <div className={className} style={{ width: widthPercentage, flexBasis: widthPercentage }}>
      <SortableItem id={id}>
        {({ isDragging }) => (
          <div className="box-border p-2">
            <div className={isDragging ? 'invisible w-full opacity-0' : undefined}>
              <div className="relative box-border rounded-sm border-base-border bg-white">
                <SortableDragArea className="w-full">
                  <div
                    className={cn('relative h-0 pt-[100%]', {
                      '[&_[data-rmiz-content="found"]_img]:cursor-pointer!': !hasZoomPreview
                    })}
                  >
                    {isImage && (
                      <Zoom isDisabled={!hasZoomPreview}>
                        <img
                          className="absolute top-1/2 left-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2 object-contain"
                          src={imagePreview}
                          alt="upload file preview"
                        />
                      </Zoom>
                    )}
                    {isVideo && <img className={styles.video} src={videoThumbnailImage} alt="video thumbnail" />}
                    {isEmptyImage && (
                      <a download={url} href={url}>
                        <img className={styles.video} src={imagePreview} alt="empty thumbnail" />
                      </a>
                    )}
                  </div>
                </SortableDragArea>

                {!disabled && (
                  <span className={styles.button} onClick={onDelete}>
                    <Icon icon="close" size={8} variant="inherit" />
                  </span>
                )}
              </div>

              {hasProgressBar ? <ProgressBar value={uploadProgress.progress} /> : <div className="h-1" />}

              <p
                className="mt-0.5 overflow-hidden text-xs text-ellipsis whitespace-nowrap"
                title={getDecodedUrlFileName(preview)}
              >
                {getDecodedUrlFileName(preview)}
              </p>
            </div>
          </div>
        )}
      </SortableItem>
    </div>
  );
};

const styles = {
  video: 'absolute top-1/2 left-1/2 w-full -translate-1/2',
  button: cn(
    'absolute top-0 right-0 z-1 inline-flex size-6 cursor-pointer items-center justify-center rounded-full bg-black-90 p-1 text-white',
    'border-[2px] border-black-10',
    '-top-2 -right-2 transition-colors duration-300 ease-in-out hover:opacity-80'
  )
};
